import HorizontalScroller from 'core-system/HorizontalScroller'
import CreditCardIcon from 'core-system/Icons/Accounts/CreditCard'
import * as FlexIcons from 'core-system/Icons/Flex'
import { Car as CarIcon } from 'core-system/Icons/modeTypes'
import CouponIcon from 'core-system/Icons/Programs/Coupon'
import OneTimePurchaseIcon from 'core-system/Icons/Micromobility/OneTimePurchase'
import {
  Flex as FlexIcon,
  Micromobility as MicromobilityIcon,
} from 'core-system/Icons/Sidebar'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import pxToRem from 'core-system/utils/pxToRem'
import { EmployeeTransitPass } from 'redux/transitPass/transitPassTypes'
import FormattingUtils from 'shared/FormattingUtils'
import React from 'react'
import styled from 'styled-components'
import {
  BenefitIcon,
  buildCommuterBenefits,
  CommuterBenefitInput,
  CommuterBenefitItem,
} from './commuterBenefitItems'

const Section = styled.div`
  margin-top: 1rem;
`

const BenefitCard = styled.div`
  flex: 0 0 11rem;
  margin-right: 0.75rem;
  padding: 0.75rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: 0.5rem;
  background-color: ${(props) => props.theme.palette.white};
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`

const TitleBlock = styled.div`
  min-width: 0;
`

const IconWell = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.3125rem;
  background-color: ${(props) => props.theme.palette.secondary.purple200};
  border: 1px solid ${(props) => props.theme.palette.secondary.purple300};
`

const Title = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Subtitle = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Amount = styled(Text)`
  margin-top: 0.75rem;
`

const RideTrack = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-top: 0.5rem;
`

const RideMark = styled.span<{ $filled: boolean }>`
  width: 0.875rem;
  height: 0.25rem;
  border-radius: 999px;
  background-color: ${(props) =>
    props.$filled
      ? props.theme.palette.secondary.purple500
      : props.theme.palette.grey.grey200};
`

const MembershipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.75rem;
`

const StatusDot = styled.span<{ $active: boolean }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${(props) =>
    props.$active
      ? props.theme.palette.success.green500
      : props.theme.palette.text.secondary};
`

const iconFor = (icon: BenefitIcon) => {
  const color = palette.text.secondary
  const size = { width: 16, height: 16, color }
  switch (icon) {
    case 'transit':
      return <FlexIcons.Transit {...size} />
    case 'parking':
      return <FlexIcons.Parking {...size} />
    case 'flex':
      return <FlexIcon {...size} />
    case 'grh':
      return <CarIcon {...size} />
    case 'micromobility':
      return <MicromobilityIcon {...size} />
    case 'otp':
      return <OneTimePurchaseIcon {...size} />
    case 'coupon':
      return <CouponIcon {...size} />
    case 'transitPass':
      return <CreditCardIcon {...size} />
  }
}

const RideMeter = ({ item }: { item: CommuterBenefitItem }) => {
  if (item.ridesAllowed == null || item.ridesLeft == null) {
    return <Amount variant='h5'>--</Amount>
  }
  const used = item.ridesAllowed - item.ridesLeft
  return (
    <>
      <Amount variant='h5'>
        {item.ridesLeft} / {item.ridesAllowed} left
      </Amount>
      <RideTrack>
        {Array.from({ length: item.ridesAllowed }).map((_, index) => (
          <RideMark key={index} $filled={index < used} />
        ))}
      </RideTrack>
    </>
  )
}

const membershipStatusLabel = (status: EmployeeTransitPass['status']) => {
  if (status === 'ACTIVE') {
    return 'Active'
  }
  if (status === 'REQUESTED') {
    return 'Requested'
  }
  if (status === 'ORDERED') {
    return 'Ordered'
  }
  if (status === 'VERIFYING') {
    return 'Verifying'
  }
  return 'Deactivated'
}

const BenefitValue = ({ item }: { item: CommuterBenefitItem }) => {
  if (item.statusLabel) {
    return (
      <Amount variant='h5' hideOverflow title={item.statusLabel}>
        {item.statusLabel}
      </Amount>
    )
  }
  if (item.membershipStatus) {
    const isActive = item.membershipStatus === 'ACTIVE'
    return (
      <>
        <MembershipRow>
          <StatusDot $active={isActive} />
          <Text variant='action3' textColor={isActive ? palette.success.green800 : undefined}>
            {membershipStatusLabel(item.membershipStatus)}
          </Text>
        </MembershipRow>
        <Text variant='caption' textColor={palette.text.secondary}>
          Membership
        </Text>
      </>
    )
  }
  if (item.ridesAllowed != null || item.icon === 'grh') {
    return <RideMeter item={item} />
  }
  return (
    <Amount variant='h5'>
      {FormattingUtils.formatDollar(item.amountCents || 0)}
    </Amount>
  )
}

const SectionLabel = () => (
  <Text
    variant='caption'
    textColor={palette.text.secondary}
    letterSpacing='0.04em'
    style={{ textTransform: 'uppercase' }}
  >
    Balances & Benefits
  </Text>
)

const CommuterBalancesBenefits = (props: CommuterBenefitInput) => {
  const items = buildCommuterBenefits(props)

  if (items.length === 0) {
    return (
      <Section>
        <SectionLabel />
        <Text variant='body2' marginTop='0.75rem' textColor={palette.text.secondary}>
          No Benefits Yet
        </Text>
      </Section>
    )
  }

  return (
    <Section>
      <SectionLabel />
      <HorizontalScroller
        translationPerClick={176}
        opacityWidth={pxToRem(48)}
        marginTop='0.75rem'
      >
        {items.map((item) => (
          <BenefitCard key={item.id}>
            <TitleRow>
              <IconWell>{iconFor(item.icon)}</IconWell>
              <TitleBlock>
                <Title variant='action4' title={item.title}>
                  {item.title}
                </Title>
                {item.subtitle ? (
                  <Subtitle
                    variant='caption'
                    textColor={palette.text.secondary}
                    title={item.subtitle}
                  >
                    {item.subtitle}
                  </Subtitle>
                ) : null}
              </TitleBlock>
            </TitleRow>
            <BenefitValue item={item} />
          </BenefitCard>
        ))}
      </HorizontalScroller>
    </Section>
  )
}

export default CommuterBalancesBenefits
